// HEAL-44a §5.2 — полноэкранный просмотр бланка-фотографии.
//
// Фото смотрим внутри приложения, а не отдаём системе: это большинство домашних
// загрузок, и выкидывать человека в галерею ради собственного же файла — плохой
// обмен. PDF, наоборот, уходит системному просмотрщику (см. lib/fileViewer).

import { useEffect, useRef, useState } from 'react';
import { X, ZoomIn, ZoomOut } from 'lucide-react';

type Props = {
  url: string;
  onClose: () => void;
  /**
   * Ссылка живёт 5 минут. Приложение могло пролежать свёрнутым дольше, и WebView
   * перезапросит картинку уже по истёкшей ссылке — без этого человек видел просто
   * чёрный экран без единого объяснения.
   */
  onRefresh?: () => Promise<string | null>;
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

export function ImageViewer({ url, onClose, onRefresh }: Props) {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [src, setSrc] = useState(url);
  const [failed, setFailed] = useState(false);
  const pinchStart = useRef<{ distance: number; zoom: number } | null>(null);
  // Один повтор: если и свежая ссылка не открылась, дело не в сроке жизни.
  const refreshedRef = useRef(false);

  useEffect(() => {
    setSrc(url);
    setFailed(false);
    refreshedRef.current = false;
  }, [url]);

  const handleError = async () => {
    if (refreshedRef.current || onRefresh === undefined) {
      setFailed(true);
      return;
    }

    refreshedRef.current = true;
    const fresh = await onRefresh();

    if (fresh === null) {
      setFailed(true);
      return;
    }

    setSrc(fresh);
  };

  // Esc закрывает — на вебе это ожидаемый жест, на устройстве не мешает.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return;
    pinchStart.current = { distance: touchDistance(e.touches), zoom };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2 || pinchStart.current === null) return;
    const ratio = touchDistance(e.touches) / pinchStart.current.distance;
    setZoom(clamp(pinchStart.current.zoom * ratio));
  };

  const handleTouchEnd = () => {
    pinchStart.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/95"
      role="dialog"
      aria-modal="true"
      aria-label="Исходный бланк анализа"
    >
      <div className="flex items-center justify-between p-4 pt-[calc(1rem+env(safe-area-inset-top))]">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setZoom((z) => clamp(z - 0.5))}
            disabled={zoom <= MIN_ZOOM}
            aria-label="Уменьшить"
            className="rounded-full bg-white/10 p-2 text-white disabled:opacity-30"
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setZoom((z) => clamp(z + 0.5))}
            disabled={zoom >= MAX_ZOOM}
            aria-label="Увеличить"
            className="rounded-full bg-white/10 p-2 text-white disabled:opacity-30"
          >
            <ZoomIn className="h-5 w-5" />
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="rounded-full bg-white/10 p-2 text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div
        className="flex flex-1 items-center justify-center overflow-auto p-4"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {failed ? (
          <p className="max-w-xs text-center text-sm text-white/70">
            Не удалось открыть бланк. Закройте просмотр и попробуйте ещё раз.
          </p>
        ) : (
          <img
            src={src}
            alt="Бланк анализа"
            onError={handleError}
            className="max-h-full max-w-full origin-center object-contain transition-transform"
            style={{ transform: `scale(${zoom})` }}
          />
        )}
      </div>
    </div>
  );
}

function touchDistance(touches: React.TouchList): number {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy) || 1;
}

function clamp(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}
